/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, DownloadIcon, CheckCircleIcon, SaveIcon, LockIcon } from './icons';
import { SavedOutfit, Lookbook, RetailItem, UserProfile } from '../types';
import { downloadImageFromDataUrl, getFriendlyErrorMessage, createThumbnailDataUrl } from '../lib/utils';
import Spinner from './Spinner';
import { generateLookbookImage, generateShoppingLookVideo, getVideosOperationStatus, findSimilarItems, extractVideoUrlFromOperation, downloadVideo, urlToBase64, generateMagazineNarrative } from '../services/geminiService';
import { useTranslation } from '../i18n/useTranslation';
import { apiKeys } from '../config';
import { hasFeatureAccess } from '@shared/services/userService';

declare global {
  interface AIStudio {
    hasSelectedApiKey: () => Promise<boolean>;
    openSelectKey: () => Promise<void>;
  }
  interface Window {
    aistudio?: AIStudio;
  }
}

interface LookbookCreatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  savedOutfits: SavedOutfit[];
  isLoadingApp: boolean;
  onSaveLookbook: (lookbook: Omit<Lookbook, 'id' | 'createdAt'>) => void;
  initialSelectedOutfitId?: string | null;
  userProfile: UserProfile;
}

type ViewState = 'selection' | 'image-result' | 'video-result' | 'magazine-result';
type LookbookType = 'collage' | 'shopping-video' | 'magazine';

const LookbookCreatorModal: React.FC<LookbookCreatorModalProps> = ({ isOpen, onClose, savedOutfits, isLoadingApp, onSaveLookbook, initialSelectedOutfitId, userProfile }) => {
  const { t, lang } = useTranslation();

  const [view, setView] = useState<ViewState>('selection');
  const [lookbookType, setLookbookType] = useState<LookbookType>('collage');
  const [selectedOutfitIds, setSelectedOutfitIds] = useState<Set<string>>(() => new Set<string>());

  const [isGenerating, setIsGenerating] = useState(false);
  const [lookbookImageUrl, setLookbookImageUrl] = useState<string | null>(null);
  const [lookbookName, setLookbookName] = useState('');
  const [isLookbookSaved, setIsLookbookSaved] = useState(false);

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [relatedItems, setRelatedItems] = useState<RetailItem[]>([]);
  const [videoGenerationStep, setVideoGenerationStep] = useState('');
  const [aspectRatio, setAspectRatio] = useState<'16:9' | '9:16' | '1:1'>('16:9');
  const [isSocialLayout, setIsSocialLayout] = useState(false);
  const [socialCaption, setSocialCaption] = useState('');
  const [isGeneratingCaption, setIsGeneratingCaption] = useState(false);
  const [editorialTheme, setEditorialTheme] = useState('');
  const [editorialNarrative, setEditorialNarrative] = useState('');

  const [apiKeyStatus, setApiKeyStatus] = useState<'unknown' | 'ready' | 'needed'>('unknown');
  const [error, setError] = useState<string | null>(null);
  const pollingRef = useRef<number | null>(null);

  const canCreateVideo = hasFeatureAccess(userProfile, 'runway_video', 'advanced');
  const canCreateMagazine = hasFeatureAccess(userProfile, 'magazine_lookbook', 'premium');

  useEffect(() => {
    if (isOpen) {
      if (initialSelectedOutfitId) {
        setSelectedOutfitIds(new Set([initialSelectedOutfitId]));
        setLookbookType('collage');
      } else {
        setSelectedOutfitIds(new Set());
      }
      setView('selection');
      setLookbookImageUrl(null);
      setVideoUrl(null);
      setRelatedItems([]);
      setError(null);
      setLookbookName('');
      setIsLookbookSaved(false);
      setAspectRatio('16:9');
      setIsSocialLayout(false);
      setSocialCaption('');
      setEditorialTheme('');
      setEditorialNarrative('');

      if (window.aistudio) {
        window.aistudio.hasSelectedApiKey().then(hasKey => {
          setApiKeyStatus(hasKey ? 'ready' : 'needed');
        });
      } else {
        setApiKeyStatus('needed'); // Assume needed if aistudio is not available
      }
    } else {
      // Clear any running polling when modal closes
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
    }
  }, [isOpen, initialSelectedOutfitId]);

  const handleSelectOutfit = useCallback((outfitId: string) => {
    setSelectedOutfitIds(prev => {
      const newSet = new Set(prev);
      if (lookbookType === 'shopping-video') {
        // Single selection for video
        if (newSet.has(outfitId)) {
          newSet.delete(outfitId);
        } else {
          newSet.clear();
          newSet.add(outfitId);
        }
      } else {
        // Multi-selection for collage
        if (newSet.has(outfitId)) {
          newSet.delete(outfitId);
        } else {
          newSet.add(outfitId);
        }
      }
      return newSet;
    });
  }, [lookbookType]);

  const selectedOutfits = useMemo(() => {
    return savedOutfits.filter(outfit => selectedOutfitIds.has(outfit.id));
  }, [savedOutfits, selectedOutfitIds]);

  const handleGenerateCollage = useCallback(async () => {
    if (selectedOutfits.length === 0) {
      setError(t('lookbookCreatorModal_errorSelectOutfits'));
      return;
    }
    setError(null);
    setIsGenerating(true);
    try {
      const imageUrls = selectedOutfits.map(outfit => outfit.previewImageUrl).filter(Boolean) as string[];
      if (imageUrls.length !== selectedOutfits.length) throw new Error(t('lookbookCreatorModal_errorNoPreviewImages'));
      const generatedUrl = await generateLookbookImage(imageUrls, aspectRatio, isSocialLayout ? 'social' : 'collage');
      setLookbookImageUrl(generatedUrl);
      setView('image-result');
    } catch (err) {
      let friendlyError = getFriendlyErrorMessage(err, t('lookbookCreatorModal_errorGenerate'), lang, t);
      if (err instanceof Error && err.message.includes("safety polic")) {
        friendlyError = t('lookbookGenError_safety', { details: err.message });
      }
      setError(friendlyError);
    } finally {
      setIsGenerating(false);
    }
  }, [selectedOutfits, t, lang]);

  const handleGenerateMagazine = useCallback(async () => {
    if (selectedOutfits.length === 0) {
      setError(t('lookbookCreatorModal_errorSelectOutfits'));
      return;
    }
    setError(null);
    setIsGenerating(true);
    setVideoGenerationStep(t('lookbookCreatorModal_step_generatingNarrative', 'Writing editorial narrative...'));

    try {
      const narrative = await generateMagazineNarrative(selectedOutfits, editorialTheme, userProfile, lang as any);

      setEditorialNarrative(narrative);

      // Also shop the look for the items (to populate related items for the magazine)
      setVideoGenerationStep(t('lookbookCreatorModal_step_findingItems'));
      const allGarments = selectedOutfits.flatMap(o => o.layer.garments);
      if (allGarments.length > 0) {
        const { results } = await findSimilarItems({ name: t(allGarments[0].nameKey), url: allGarments[0].url });
        setRelatedItems(results.slice(0, 6));
      }

      setView('magazine-result');
    } catch (err) {
      console.error('Magazine creation error:', err);
      setError(getFriendlyErrorMessage(err, 'Failed to create magazine narrative', lang, t));
    } finally {
      setIsGenerating(false);
      setVideoGenerationStep('');
    }
  }, [selectedOutfits, editorialTheme, userProfile, t, lang]);

  // --- Video Generation flow ---
  const handleGenerateVideo = useCallback(async () => {
    const selectedOutfits = savedOutfits.filter((o) => selectedOutfitIds.has(o.id));
    if (selectedOutfits.length !== 1) {
      setError(t('lookbookCreatorModal_error_selectOneOutfit'));
      return;
    }

    const outfit = selectedOutfits[0];
    if (!outfit.previewImageUrl) {
      setError(t('lookbookCreatorModal_error_noPreviewForVideo'));
      return;
    }

    // Check API Key status
    if (apiKeyStatus === 'needed' && window.aistudio) {
      await window.aistudio.openSelectKey();
      setApiKeyStatus('ready');
    }

    setIsGenerating(true);
    setError(null);
    setVideoGenerationStep(t('lookbookCreatorModal_step_starting'));

    try {
      // 1. Convert image to base64 using centralized helper
      const base64Image = await urlToBase64(outfit.previewImageUrl);

      // 2. Start generation
      const prompt = t('lookbookCreatorModal_videoPrompt', { outfitName: outfit.name });
      const { operation } = await generateShoppingLookVideo(base64Image, 'image/jpeg', prompt, aspectRatio);

      setVideoGenerationStep(t('lookbookCreatorModal_step_generatingVideo'));

      // 3. Poll for result with robust loop
      let currentOp = operation;
      let isDone = false;
      const startTime = Date.now();
      const MAX_POLLING_TIME = 300000; // 5 minutes

      while (!isDone) {
        if (Date.now() - startTime > MAX_POLLING_TIME) {
          throw new Error(t('error_timeout'));
        }

        await new Promise(r => setTimeout(r, 5000));

        // Let the service handle extraction
        const { operation: updatedOperation } = await getVideosOperationStatus(currentOp);
        currentOp = updatedOperation;

        if (currentOp.done || currentOp.response || currentOp.error) {
          isDone = true;
        }
        // Fallback to state check
        if ((currentOp as any).metadata?.state === 'SUCCEEDED' || (currentOp as any).metadata?.state === 'FAILED') {
          isDone = true;
        }
      }

      if (currentOp.error) {
        // Handle common API key issue if entity not found (might mean key not propagated correctly to operation)
        const errorMessage = (currentOp.error as any).message || '';
        if (errorMessage.includes('Requested entity was not found')) {
          setApiKeyStatus('needed');
        }
        throw new Error(errorMessage || t('lookbookCreatorModal_error_videoGeneration'));
      }

      // 4. Extract result using helper
      const { videoUrl: directUrl, downloadUri } = extractVideoUrlFromOperation(currentOp);

      let finalVideoUrl = '';
      if (directUrl) {
        finalVideoUrl = directUrl;
      } else if (downloadUri) {
        // 5. Download video if URI requires proxy/key
        finalVideoUrl = await downloadVideo(downloadUri, apiKeys.gemini);
      } else {
        throw new Error(t('lookbookCreatorModal_error_noVideoLink'));
      }

      setVideoUrl(finalVideoUrl);

      // 6. Shop the look (Discovery)
      setVideoGenerationStep(t('lookbookCreatorModal_step_findingItems'));
      try {
        const outfitName = outfit.layer.mainGarment?.nameKey ? t(outfit.layer.mainGarment.nameKey) : outfit.name;
        const mainGarmentUrl = outfit.layer.mainGarment?.url || '';
        if (mainGarmentUrl) {
          const { results } = await findSimilarItems({ name: outfitName, url: mainGarmentUrl });
          setRelatedItems(results.slice(0, 4));
        }
      } catch (shopErr) {
        console.error('Shopping discovery failed', shopErr);
      }

      setVideoGenerationStep('');
      setView('video-result');
    } catch (err) {
      console.error('Video creation error:', err);
      setError(getFriendlyErrorMessage(err, t('lookbookCreatorModal_error_videoStart'), lang, t));
    } finally {
      setIsGenerating(false);
    }
  }, [savedOutfits, selectedOutfitIds, apiKeyStatus, t, lang, aspectRatio]);

  const handleGenerateCaption = async (platform: 'instagram' | 'linkedin' | 'twitter') => {
    if (selectedOutfits.length === 0) return;
    setIsGeneratingCaption(true);
    try {
      const { generateSocialCaption } = await import('../services/geminiService');
      const caption = await generateSocialCaption(selectedOutfits[0], platform, lang);
      setSocialCaption(caption);
    } catch (e) {
      console.error("Failed to generate caption", e);
    } finally {
      setIsGeneratingCaption(false);
    }
  };


  const handleSave = async () => {
    if (!lookbookName.trim()) {
      setError(t('lookbookCreatorModal_error_enterName'));
      return;
    }

    let finalLookbookImageUrl = lookbookImageUrl;
    if (lookbookType === 'collage' && lookbookImageUrl) {
      try {
        finalLookbookImageUrl = await createThumbnailDataUrl(lookbookImageUrl, 800, 450, 'image/jpeg', 0.8);
      } catch (e) {
        console.error("Failed to create lookbook thumbnail", e);
      }
    }

    const lookbookToSave: Omit<Lookbook, 'id' | 'createdAt'> = {
      name: lookbookName.trim(),
      type: lookbookType,
      outfitIds: Array.from(selectedOutfitIds),
      lookbookImageUrl: finalLookbookImageUrl || undefined,
      videoUrl: videoUrl || undefined,
      relatedRetailItems: relatedItems.length > 0 ? relatedItems : undefined,
      editorialNarrative: lookbookType === 'magazine' ? editorialNarrative : undefined,
    };
    onSaveLookbook(lookbookToSave);
    setIsLookbookSaved(true);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          aria-modal="true" role="dialog"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-xl overflow-hidden dark:bg-gray-800"
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-serif tracking-wider text-gray-800 dark:text-gray-100">{t('lookbookCreatorModal_title')}</h2>
              <button onClick={onClose} className="p-1 rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700" aria-label={t('lookbookCreatorModal_close')}>
                <XIcon className="w-6 h-6" />
              </button>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={view}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="p-6 overflow-y-auto custom-scrollbar flex-grow"
              >
                {view === 'selection' && (
                  <div>
                    {/* Tabs */}
                    <div className="flex justify-center border-b border-gray-200 dark:border-gray-700 mb-6">
                      <button onClick={() => setLookbookType('collage')} className={`px-6 py-2 font-semibold text-lg ${lookbookType === 'collage' ? 'border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        {t('lookbookCreatorModal_tabCollage')}
                      </button>
                      <button
                        onClick={() => setLookbookType('shopping-video')}
                        disabled={!canCreateVideo}
                        title={!canCreateVideo ? t('feature_locked_advanced_generic') : ''}
                        className={`relative px-6 py-2 font-semibold text-lg ${lookbookType === 'shopping-video' ? 'border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400'} ${!canCreateVideo ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {t('lookbookCreatorModal_tabVideo')}
                        {!canCreateVideo && <LockIcon className="w-4 h-4 absolute top-1 right-1 text-gray-400" />}
                      </button>
                      <button
                        onClick={() => setLookbookType('magazine')}
                        disabled={!canCreateMagazine}
                        title={!canCreateMagazine ? t('feature_locked_premium_generic') : ''}
                        className={`relative px-6 py-2 font-semibold text-lg ${lookbookType === 'magazine' ? 'border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400'} ${!canCreateMagazine ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {t('lookbookCreatorModal_tabMagazine', 'Magazine')}
                        {!canCreateMagazine && <LockIcon className="w-4 h-4 absolute top-1 right-1 text-gray-400" />}
                      </button>
                    </div>

                    {/* Options */}
                    <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{t('lookbookCreatorModal_aspectRatio')}</label>
                        <div className="flex gap-2">
                          {(['16:9', '9:16', '1:1'] as const).map(ratio => (
                            <button
                              key={ratio}
                              onClick={() => setAspectRatio(ratio)}
                              className={`px-3 py-1 text-sm rounded-md border transition-colors ${aspectRatio === ratio ? 'bg-indigo-500 border-indigo-500 text-white' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                            >
                              {ratio}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-col justify-end">
                        <label className="flex items-center gap-2 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={isSocialLayout}
                            onChange={e => setIsSocialLayout(e.target.checked)}
                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                            {t('lookbookCreatorModal_socialLayout')}
                          </span>
                        </label>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 ml-6">{t('lookbookCreatorModal_socialLayoutDesc')}</p>
                      </div>
                    </div>

                    {lookbookType === 'magazine' && (
                      <div className="bg-amber-50 dark:bg-amber-900/10 p-4 rounded-xl mb-6">
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('lookbookCreatorModal_themeLabel', 'Editorial Theme (Optional)')}</label>
                        <input
                          type="text"
                          value={editorialTheme}
                          onChange={e => setEditorialTheme(e.target.value)}
                          placeholder={t('lookbookCreatorModal_themePlaceholder', 'e.g. Minimalist Spring, Corporate Leadership...')}
                          className="w-full p-2 border border-amber-200 dark:border-amber-700 rounded-md bg-white dark:bg-gray-800 focus:ring-amber-500 outline-none"
                        />
                      </div>
                    )}

                    <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
                      {lookbookType === 'collage' ? t('lookbookCreatorModal_instructionsCollage') :
                        lookbookType === 'shopping-video' ? t('lookbookCreatorModal_instructionsVideo') :
                          t('lookbookCreatorModal_instructionsMagazine', 'Select outfits to feature in your interactive editorial magazine.')}
                    </p>

                    {/* Outfit Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {savedOutfits.map(outfit => (
                        <button key={outfit.id} onClick={() => handleSelectOutfit(outfit.id)}
                          className={`relative aspect-[3/4] rounded-lg overflow-hidden group border-2 ${selectedOutfitIds.has(outfit.id) ? 'border-indigo-500' : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'}`}
                        >
                          <img src={outfit.previewImageUrl || undefined} alt={outfit.name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <p className="text-white text-sm font-semibold text-center p-1">{outfit.name}</p>
                          </div>
                          {selectedOutfitIds.has(outfit.id) && (
                            <div className="absolute top-2 right-2 bg-indigo-500 text-white rounded-full p-1">
                              <CheckCircleIcon className="w-5 h-5" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {view === 'image-result' && lookbookImageUrl && (
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-1 text-center bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl border dark:border-gray-700">
                      <img src={lookbookImageUrl} alt={t('lookbookCreatorModal_generatedImageAlt')} className={`max-w-full mx-auto shadow-lg rounded-lg ${aspectRatio === '9:16' ? 'max-h-[60vh] w-auto' : 'max-h-[50vh] object-contain'}`} loading="lazy" decoding="async" />
                    </div>
                    <div className="w-full md:w-80 flex flex-col gap-4">
                      <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border dark:border-gray-700 flex-grow">
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{t('lookbookCreatorModal_socialCaption')}</label>
                        <textarea
                          value={socialCaption}
                          onChange={e => setSocialCaption(e.target.value)}
                          placeholder={t('lookbookCreatorModal_captionPlaceholder')}
                          className="w-full h-40 p-3 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 resize-none focus:ring-2 focus:ring-indigo-500 outline-none"
                        />
                        <div className="flex flex-wrap gap-2 mt-3">
                          {(['instagram', 'linkedin', 'twitter'] as const).map(plat => (
                            <button
                              key={plat}
                              onClick={() => handleGenerateCaption(plat)}
                              disabled={isGeneratingCaption}
                              className="text-[10px] px-2 py-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                            >
                              {isGeneratingCaption ? '...' : plat.charAt(0).toUpperCase() + plat.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {view === 'video-result' && videoUrl && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                      <video src={videoUrl} controls autoPlay loop className="w-full rounded-lg shadow-lg" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">{t('lookbookCreatorModal_shopTheLook')}</h3>
                      <div className="space-y-2 max-h-[50vh] overflow-y-auto custom-scrollbar">
                        {relatedItems.map((item, index) => (
                          <a href={item.productUrl} key={index} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                            <img src={item.imageUrl} alt={item.name} className="w-12 h-12 object-cover rounded-md flex-shrink-0" loading="lazy" decoding="async" />
                            <div>
                              <p className="text-xs font-semibold line-clamp-2">{item.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{item.price}</p>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {view === 'magazine-result' && (
                  <div className="flex flex-col gap-6 items-center">
                    <div className="text-center max-w-2xl bg-amber-50 dark:bg-amber-900/10 p-6 rounded-xl border border-amber-200 dark:border-amber-800 shadow-inner">
                      <h3 className="text-2xl font-serif text-amber-800 dark:text-amber-500 mb-4">{t('lookbookCreatorModal_magazinePreviewTitle', 'Editorial Narrative Generated')}</h3>
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line text-left italic font-serif leading-relaxed">
                        {editorialNarrative}
                      </p>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('lookbookCreatorModal_magazineSaveHint', 'Save this lookbook to interact with the full magazine experience.')}
                    </p>
                  </div>
                )}

              </motion.div>
            </AnimatePresence>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              {error && <p className="text-red-500 text-sm mb-2 text-center">{error}</p>}
              {isGenerating && videoGenerationStep && <p className="text-indigo-600 dark:text-indigo-400 text-sm mb-2 text-center">{videoGenerationStep}</p>}

              {view === 'selection' && (
                <div className="flex justify-end">
                  <button onClick={lookbookType === 'collage' ? handleGenerateCollage : lookbookType === 'shopping-video' ? handleGenerateVideo : handleGenerateMagazine} disabled={isGenerating || selectedOutfitIds.size === 0}
                    className="bg-indigo-600 text-white font-semibold py-2 px-5 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center">
                    {isGenerating && <Spinner className="w-5 h-5 mr-2" />}
                    {isGenerating ? t('lookbookCreatorModal_generating') : t('lookbookCreatorModal_generateButton')}
                  </button>
                </div>
              )}

              {(view === 'image-result' || view === 'video-result' || view === 'magazine-result') && (
                <div className="flex justify-between items-center">
                  <input type="text" value={lookbookName} onChange={e => setLookbookName(e.target.value)} placeholder={t('lookbookCreatorModal_namePlaceholder')}
                    className="flex-grow p-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600" />
                  <div className="flex gap-2 ml-4">
                    {(view === 'image-result' && lookbookImageUrl) && <button onClick={() => downloadImageFromDataUrl(lookbookImageUrl, `${lookbookName || t('lookbookCreatorModal_defaultFileName')}.png`)} title={t('lookbookCreatorModal_download')} className="bg-gray-200 text-gray-800 font-semibold py-2 px-4 rounded-lg hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-50"><DownloadIcon className="w-5 h-5" /></button>}
                    <button onClick={handleSave} disabled={isLookbookSaved || !lookbookName.trim()}
                      className="bg-indigo-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center">
                      <SaveIcon className="w-5 h-5 mr-2" />
                      {isLookbookSaved ? t('lookbookCreatorModal_saved') : t('lookbookCreatorModal_saveButton')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LookbookCreatorModal;